import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import PublicNav from "../../components/layout/PublicNav";
import Footer, { CONTACT } from "../../components/layout/Footer";
import { getService, SERVICES } from "../../data/services";
import QuickInquiryForm from "../../components/QuickInquiryForm";

/** Map service slug → pre-filled Mode for the inquiry form */
const SLUG_TO_MODE = {
  "air-freight": "Air",
  "ocean-freight": "Sea",
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);
  const Icon = service.icon;
  const defaultMode = SLUG_TO_MODE[slug] || "";

  return (
    <div className="App">
      <PublicNav />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative bg-[#F8F9FA] py-14 md:py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Link
              to="/#services"
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#DC2A1C] mb-6 transition-colors"
              data-testid="service-back"
            >
              <ArrowLeft className="w-4 h-4" /> All services
            </Link>
            <div className="w-12 h-12 rounded-lg bg-[#DC2A1C]/10 grid place-items-center mb-5">
              <Icon className="w-6 h-6 text-[#DC2A1C]" strokeWidth={2} />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-3">
              {service.title}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#1A1A1A] leading-[1.05]">
              {service.hero}
            </h1>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#service-quote"
                className="tl-btn inline-flex items-center gap-2"
                data-testid="service-quote-cta"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("service-quote")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Request a Quote <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={`tel:${CONTACT.phoneHref}`}
                className="tl-btn-outline"
                data-testid="service-call-cta"
              >
                Call {CONTACT.phone}
              </a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]">
            <img
              src={service.img}
              alt={service.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* ── Content + Sidebar ─────────────────────────────────── */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

            {/* Left: content — 62% on lg+ */}
            <div className="w-full lg:w-[62%] min-w-0">
              <div className="grid md:grid-cols-2 gap-12 mb-12">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] leading-tight mb-5">
                    What we do.
                  </h2>
                  <div className="space-y-4 text-gray-600 leading-relaxed text-sm">
                    {service.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-4">
                    Our Expertise
                  </h3>
                  <ul className="space-y-3" data-testid="service-features">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <CheckCircle2
                          className="w-5 h-5 text-[#DC2A1C] flex-shrink-0 mt-0.5"
                          strokeWidth={2}
                        />
                        <span className="text-[#1A1A1A] text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right sidebar: form — 38% on lg+ */}
            <div id="service-quote" className="w-full lg:w-[38%] lg:sticky lg:top-6">
              <QuickInquiryForm defaultMode={defaultMode} compact={true} />
            </div>
          </div>
        </div>
      </section>

      {/* ── Other Services ────────────────────────────────────── */}
      <section className="bg-[#F8F9FA] py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-3">
            Other Services
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-[#1A1A1A] mb-10">Explore more.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {others.map((s) => {
              const SOther = s.icon;
              return (
                <Link
                  to={`/services/${s.slug}`}
                  key={s.slug}
                  className="group bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex"
                  data-testid={`service-related-${s.slug}`}
                >
                  <div className="w-1/3 overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex-1">
                    <div className="w-8 h-8 rounded-lg bg-[#DC2A1C]/10 grid place-items-center mb-3">
                      <SOther className="w-4 h-4 text-[#DC2A1C]" strokeWidth={2} />
                    </div>
                    <h3 className="font-bold text-[#1A1A1A] text-sm leading-tight mb-1">
                      {s.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{s.short}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
